import { useToast } from '@aicon-dev/caas-ui';
import { Dialog } from 'antd-mobile';
import { AxiosError, isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

interface ApiErrorHandlerOptions {
  /**
   * 에러 메시지를 화면에 출력할 때 사용할 매체
   *
   * @default 'toast'
   */
  media?: 'toast' | 'modal';
  /**
   * 에러가 발생해도 에러 메시지만을 반환하고 화면에 출력하지 않습니다.
   */
  silence?: boolean;
}
export type ApiErrorMessage = string;
export type ApiErrorType = string;
export type ApiErrorResponse = [ApiErrorMessage, ApiErrorType];

type catchErrorResponse = ApiErrorResponse;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ApiError = AxiosError | any;

export const useCatch = () => {
  const { t, i18n } = useTranslation(['error_code', 'error_page']);
  const toast = useToast();

  const getApiErrorMessage = (error: ApiError): ApiErrorResponse => {
    if (
      !isAxiosError(error) &&
      error?.message?.includes('Failed to fetch dynamically imported module')
    ) {
      return [t('error_code:error_network'), 'page-network-error'];
    }
    if (!error?.response) {
      switch (error?.message) {
        case 'Network Error':
          return [t('error_code:error_network'), error?.message];
        default:
          return [t('error_code:error_unknown'), 'unknown'];
      }
    }

    if (error?.response?.data?.code && error.response.data.code !== -1) {
      console.log('Catched backend error code: ' + error?.response?.data?.code);

      const i18nResource = i18n.getResource(
        i18n.resolvedLanguage || 'en',
        'error_code',
        error?.response?.data?.code,
      );

      return [
        i18nResource || error?.response?.data?.detail || error?.response?.data?.code,
        error?.response?.data?.code,
      ];
    }

    switch (error?.response?.status) {
      case 404:
        return [t('error_code:error_404'), error?.response?.status.toString()];
      default:
        return [t('error_code:error_unknown'), 'unknown'];
    }
  };

  const catchError = (error?: ApiError, options?: ApiErrorHandlerOptions): catchErrorResponse => {
    /**
     * 에러 코드에 맞는 에러 메시지를 찾습니다.
     */
    const response = getApiErrorMessage(error);
    const [errorMessage, errorType] = response;

    /**
     * silence 옵션이 true일 경우
     * 화면에 에러 메시지를 출력하지 않습니다.
     */
    if (options?.silence) return response;

    if (errorType === 'unknown') {
      Dialog.alert({
        title: errorMessage,
        content: JSON.stringify(error.response?.data),
        confirmText: t('global:m_button_confirm'),
      });
      return response;
    }

    /**
     * media 옵션에 따라 화면에 에러를 출력하는
     * 방식을 결정합니다.
     */
    switch (options?.media) {
      case 'modal':
        Dialog.alert({
          title: errorMessage,
          content: JSON.stringify(error.response?.data),
          confirmText: t('global:m_button_confirm'),
        });
        break;
      case 'toast':
      default:
        toast.error(errorMessage);
    }

    return response;
  };

  return { catchError };
};
